import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  TextField,
  InputLabel,
  Select,
  OutlinedInput,
} from '@mui/material'
import { CashRefund } from 'mdi-material-ui'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import Moment from 'moment'
import { inject } from 'mobx-react'
import moment from 'moment'
import React from 'react'
import { Helmet } from 'react-helmet'
import ReactPaginate from 'react-paginate'
import { IStores } from 'src/Stores'
import ProcessReceivedCheckModal from 'src/common/ProcessReceivedCheckModal'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import AppealLogModal from 'src/search/AppealLogModal'
import { searchProviderRefunds } from 'src/services/SearchService'
import { formatCurrency } from 'src/utils/Formatter'
import { ProviderRefundsSearchRequest } from 'src/viewModels/ProviderRefundsSearchRequest'
import { ProviderRefundsSummaryResult } from 'src/viewModels/ProviderRefundsSummaryResult'
import { pagedList } from 'src/viewModels/pagedList'
import ReceiveRefundPaymentDialog from './ReceiveRefundPaymentDialog'
import { ProcessReceivedCheckResults } from 'src/viewModels/ProcessReceivedCheckResult'
import { DropdownOption } from 'src/viewModels/DropdownOption'

interface IProviderRefundsProps {
  closeAppealLogModal?: () => void
  selectedClaimId?: string
  isAppealLogModalOpen?: boolean
  loadReceiveRefundPaymentData: (cms1500Id: string) => void
  setSelectedCMS1500Id: (cms1500Id?: string) => void
  setUpdateParentStateAfterModal: (parentfunction?: () => void) => void
  closePaymentModal: () => void
  isPaymentResultOpen?: boolean
  paymentResults?: ProcessReceivedCheckResults
  isLoading: boolean
  providerGroupList?: DropdownOption[]
  getProviderGroupDropdown?: () => void
  // Search persistence support (matching Receive Payments pattern)
  setProviderRefundRequest?: (
    providerRefundSearchRequest: ProviderRefundsSearchRequest
  ) => void
  providerRefundRequest?: ProviderRefundsSearchRequest
  rerouteToPath?: (path: string) => void
}

class ProviderRefunds extends React.Component<IProviderRefundsProps> {
  public componentDidMount() {
    if (this.props.setUpdateParentStateAfterModal) {
      this.props.setUpdateParentStateAfterModal(this.getProviderRefundsData)
    }
    this.props.getProviderGroupDropdown!()
  }

  private getProviderRefundsData = async (savedSearch: boolean = false) => {
    this.setState({ gridLoading: true })
    const { page, pageSize } = this.state

    const requestData = new ProviderRefundsSearchRequest()
    requestData.page = page
    requestData.pageSize = pageSize
    requestData.patientFirstName = this.state.searchPatientFirstName || undefined
    requestData.patientLastName = this.state.searchPatientLastName || undefined
    requestData.eocLegacy = this.state.searchEocLegacy || undefined
    requestData.providerGroupId = this.state.searchProviderGroup || undefined
    requestData.dosStartDate = this.state.searchDosStartDate || undefined
    requestData.dosEndDate = this.state.searchDosEndDate || undefined

    await searchProviderRefunds(requestData).then((response: any) => {
      this.setState(
        {
          gridData: response.items,
          pageCount: response.totalPages,
          totalItems: response.totalItems,
          gridLoading: false,
        },
        () => {
          this.setPaginationOffsetData()
        }
      )
      if (response.totalItems > 0) {
        if (this.props.getProviderGroupDropdown) {
          this.props.getProviderGroupDropdown()
        }
      }

      if (savedSearch && this.props.setProviderRefundRequest) {
        this.props.setProviderRefundRequest(requestData)
      }
    })
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems == 0
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getProviderRefundsData())
  }

  private mapGridData = () => {
    if (this.isGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={6} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => (
        <TableRow className="gridPadding" key={index} style={{ paddingLeft: '30px' }}>
          <TableCell style={{ whiteSpace: 'nowrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => this.openClaimItem(record.appealId!)}
            >
              Open
            </Button>
          </TableCell>
          <TableCell>{record.patientName}</TableCell>
          <TableCell>{record.eocLegacyGroup}</TableCell>
          <TableCell align="right">
            {moment(record.dateOfService).format('MM/DD/YYYY')}
          </TableCell>
          <TableCell>{record.providerGroupName}</TableCell>
          <TableCell align="right">
            {formatCurrency(record.refundBalance.toString())}
          </TableCell>
        </TableRow>
      ))
    }
  }

  public state = {
    gridData: new pagedList().items as ProviderRefundsSummaryResult[],
    page: 1,
    pageCount: 0,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 20,
    gridLoading: false,
    searchPatientFirstName: this.props.providerRefundRequest?.patientFirstName || '',
    searchPatientLastName: this.props.providerRefundRequest?.patientLastName || '',
    searchEocLegacy: this.props.providerRefundRequest?.eocLegacy || '',
    searchProviderGroup: this.props.providerRefundRequest?.providerGroupId || '',
    searchDosStartDate: this.props.providerRefundRequest?.dosStartDate || '',
    searchDosEndDate: this.props.providerRefundRequest?.dosEndDate || '',
  }

  private search = (event: any) => {
    this.setState({ page: 1 }, () => this.getProviderRefundsData(true))
    event.preventDefault()
  }

  private clearSearch = () => {
    const select_box = document.getElementById('providerGroupSelect') as HTMLSelectElement

    if (select_box != undefined) {
      select_box.selectedIndex = 0
    }
    const start = document.getElementById('startDate') as HTMLInputElement

    if (start != undefined) {
      start.value = ''
    }
    const end = document.getElementById('endDate') as HTMLInputElement

    if (end != undefined) {
      end.value = ''
    }

    this.setState(
      {
        searchPatientFirstName: '',
        searchPatientLastName: '',
        searchEocLegacy: '',
        searchProviderGroup: '',
        searchDosStartDate: '',
        searchDosEndDate: '',
        page: 1,
      },
      () => {
        this.setState({ gridData: new pagedList().items, pageCount: 0, totalItems: 0 })
        if (this.props.setProviderRefundRequest) {
          this.props.setProviderRefundRequest(new ProviderRefundsSearchRequest())
        }
      }
    )
  }

  private handlePatientFirstNameChange = (event: any) => {
    this.setState({ searchPatientFirstName: event.target.value })
  }

  private handlePatientLastNameChange = (event: any) => {
    this.setState({ searchPatientLastName: event.target.value })
  }

  private handleEocLegacyChange = (event: any) => {
    this.setState({ searchEocLegacy: event.target.value })
  }

  private handleDosStartDateChange = (date: any) => {
    this.setState({ searchDosStartDate: date ? Moment(date).format('YYYY-MM-DD') : '' })
  }

  private handleDosEndDateChange = (date: any) => {
    this.setState({ searchDosEndDate: date ? Moment(date).format('YYYY-MM-DD') : '' })
  }

  private setSelectedProviderGroup = (event: any) => {
    this.setState({ searchProviderGroup: event.target.value })
  }

  private openClaimItem = (appealId: string) => {
    window.open(`/workflow/claim/${appealId}/process-refunds-search`, '_blank')
  }

  public render() {
    return (
      <>
        <Helmet>
          <title>Receive Provider Refunds</title>
        </Helmet>
        <ResponsiveAppBar title="Process Refunds" pageIcon={<CashRefund />} />

        <Paper style={{ margin: '20px', height: '100%', paddingBottom: '20px' }}>
          <Grid container>
            <ProcessReceivedCheckModal />
            <ReceiveRefundPaymentDialog />
            {this.props.selectedClaimId && this.props.isAppealLogModalOpen ? (
              <AppealLogModal
                claimId={this.props.selectedClaimId}
                isOpen={this.props.isAppealLogModalOpen}
                closeModal={this.props.closeAppealLogModal!}
                groupInfo=""
              />
            ) : (
              <></>
            )}
            {(this.props.isLoading || this.state.gridLoading) && (
              <div
                style={{
                  position: 'fixed',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: '1200',
                }}
              >
                <CircularProgress size={100} />
              </div>
            )}

            <form onSubmit={this.search}>
              <Grid
                container
                style={{ padding: '10px', paddingLeft: '30px', width: '100%' }}
                xs={12}
              >
                <Typography
                  color="primary"
                  component="h6"
                  variant="h6"
                  gutterBottom
                  style={{ paddingBottom: '10px' }}
                >
                  SEARCH CRITERIA
                </Typography>
                <Grid container direction="row" spacing={2} style={{ width: '100%' }}>
                  <Grid item xs={2}>
                    <FormControl fullWidth>
                      <TextField
                        value={this.state.searchPatientFirstName}
                        onChange={this.handlePatientFirstNameChange}
                        label="Patient First Name"
                        name="patientFirst"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <FormControl fullWidth>
                      <TextField
                        value={this.state.searchPatientLastName}
                        onChange={this.handlePatientLastNameChange}
                        label="Patient Last Name"
                        name="patientLast"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <FormControl fullWidth>
                      <TextField
                        value={this.state.searchEocLegacy}
                        onChange={this.handleEocLegacyChange}
                        label="EOC/Legacy"
                        name="eocLegacy"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <FormControl style={{ width: '100%' }} variant="outlined">
                      <InputLabel shrink={true} variant="outlined">
                        Provider Group
                      </InputLabel>
                      <Select
                        variant="outlined"
                        input={<OutlinedInput notched label="Provider Group" />}
                        native={true}
                        fullWidth
                        onChange={this.setSelectedProviderGroup}
                        id="providerGroupSelect"
                        value={this.state.searchProviderGroup}
                        disabled={
                          this.props.providerGroupList == undefined ||
                          this.props.providerGroupList?.length == 0
                        }
                      >
                        <option value="">Select a provider group</option>
                        {this.props.providerGroupList?.map((providerGroup) => (
                          <option key={providerGroup.id} value={providerGroup.id}>
                            {providerGroup.description}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item container xs={4} direction="row" spacing={1}>
                    <Grid item xs={4}>
                      <DatePicker
                        label="DOS Start Date"
                        value={
                          this.state.searchDosStartDate
                            ? Moment(this.state.searchDosStartDate)
                            : null
                        }
                        onChange={this.handleDosStartDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            InputLabelProps: { shrink: true },
                          },
                          openPickerIcon: {
                            fontSize: 'small',
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <strong> - </strong>
                    </Grid>
                    <Grid item xs={4}>
                      <DatePicker
                        label="DOS End Date"
                        value={
                          this.state.searchDosEndDate
                            ? Moment(this.state.searchDosEndDate)
                            : null
                        }
                        onChange={this.handleDosEndDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            InputLabelProps: { shrink: true },
                          },
                          openPickerIcon: {
                            fontSize: 'small',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    style={{
                      marginLeft: '1px',
                      paddingBottom: '10px',
                      paddingTop: '10px',
                    }}
                  >
                    <Grid item>
                      <Button
                        type="submit"
                        color="primary"
                        size="medium"
                        variant="contained"
                      >
                        Search
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        color="secondary"
                        onClick={this.clearSearch}
                        size="medium"
                        variant="contained"
                      >
                        Clear Search
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>

            <Grid container style={{ margin: '10px', marginLeft: '0px' }} xs={12}>
              <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignContent="center"
                alignItems="flex-start"
                wrap="wrap"
              >
                <Grid item xs={12}>
                  <Table aria-label="simple table" style={{ padding: '20' }}>
                    <TableHead>
                      <TableRow className="gridPadding" style={{ paddingLeft: '30px' }}>
                        <TableCell />
                        <TableCell>Patient</TableCell>
                        <TableCell>EOC/Legacy</TableCell>
                        <TableCell align="right">DOS</TableCell>
                        <TableCell>Provider Group</TableCell>
                        <TableCell align="right">Refund Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody style={{ width: '100%' }}>{this.mapGridData()}</TableBody>
                  </Table>
                  <div className="pagination-row">
                    {this.paginationInfo()}
                    <ReactPaginate
                      previousLabel={'<'}
                      nextLabel={'>'}
                      onPageChange={this.handlePageChange}
                      forcePage={this.state.page - 1}
                      pageCount={this.state.pageCount}
                      containerClassName={'pagination'}
                      activeClassName={'active'}
                      //@ts-ignore
                      renderOnZeroPageCount={null}
                    />
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </>
    )
  }
}

const InjectedProviderRefunds = inject<
  IStores,
  IProviderRefundsProps,
  Partial<IProviderRefundsProps>,
  any
>((stores: IStores) => ({
  closeAppealLogModal: stores.cms1500.closeAppealLogModal,
  isAppealLogModalOpen: stores.cms1500.isAppealLogModalOpen,
  loadReceiveRefundPaymentData: stores.providerRefundStore.loadReceivePaymentData,
  selectedClaimId: stores.cms1500.selectedClaimId,
  setSelectedCMS1500Id: stores.providerRefundStore.setSelectedCMS1500Id,
  setUpdateParentStateAfterModal: stores.providerRefundStore.setParentUpdateFunction,
  closePaymentModal: stores.providerRefundStore.closeProcessReceivedCheck,
  isPaymentResultOpen: stores.providerRefundStore.isProcessRefundCheckOpen,
  paymentResults: stores.providerRefundStore.checkResults,
  isLoading: stores.providerRefundStore.isLoading,
  providerGroupList: stores.providerRefundStore.providerGroupList,
  getProviderGroupDropdown: stores.providerRefundStore.getProviderGroupDropdown,
  // Search persistence (matching Receive Payments pattern exactly)
  setProviderRefundRequest: stores.global.setProviderRefundRequest,
  providerRefundRequest: stores.global.providerRefundRequest,
  rerouteToPath: stores.global.rerouteToPath,
}))(ProviderRefunds)

export default InjectedProviderRefunds
